import React, { useState, useEffect, Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import Layout from "./components/Layout";
import ProtectedRoute from "./components/Auth/ProtectedRoute";
import RecruiterRoute from "./components/Auth/RecruiterRoute";
import { AdminRoute } from "./components/Auth/AdminRoute";
import { useAuth0 } from "@auth0/auth0-react";
import { Toaster } from "react-hot-toast";
import { CircularProgress, Box } from '@mui/material';
import { ThemeContextProvider } from './contexts/ThemeContext';
import LandingPage from './components/LandingPage';
import { getUserRoles } from './services/auth';
import { FloatingChatProvider } from './contexts/FloatingChatContext';
import { FloatingChatWrapper } from './components/FloatingChat/FloatingChatWrapper';
// import { useKeepAlive } from './hooks/useKeepAlive'; // DISABLED - not preventing Render backend shutdown
import { TourProvider } from './contexts/TourContext';
import { ProductTour } from './components/Tour';

// Lazy load route-level components for code splitting
// const InterviewPanel = lazy(() => import('./components/Candidate/InterviewPanel')); // REPLACED by UnifiedInterviewPanel
const UnifiedInterviewPanel = lazy(() => import('./components/Interview/UnifiedInterviewPanel').then(m => ({ default: m.UnifiedInterviewPanel })));
const InterviewHistory = lazy(() => import('./components/Candidate/InterviewHistory').then(m => ({ default: m.InterviewHistory })));
const InterviewDetails = lazy(() => import('./components/Candidate/InterviewDetails').then(m => ({ default: m.InterviewDetails })));
const InterviewSelection = lazy(() => import('./pages/InterviewSelection').then(m => ({ default: m.InterviewSelection })));
const CodingChallengePanel = lazy(() => import('./components/Candidate/CodingChallengePanel'));
// const SpecializedInterviewPanel = lazy(() => import('./components/Candidate/SpecializedInterviewPanel').then(m => ({ default: m.SpecializedInterviewPanel }))); // REPLACED by UnifiedInterviewPanel
const OnboardingFlow = lazy(() => import('./components/Onboarding').then(m => ({ default: m.OnboardingFlow })));
const RoleSelectionOnboarding = lazy(() => import('./components/Onboarding').then(m => ({ default: m.RoleSelectionOnboarding })));
const SettingsPage = lazy(() => import('./components/Settings').then(m => ({ default: m.SettingsPage })));
const RolePrepList = lazy(() => import('./pages/RolePrep/RolePrepList').then(m => ({ default: m.RolePrepList })));
const CreatePreparation = lazy(() => import('./pages/RolePrep/CreatePreparation').then(m => ({ default: m.CreatePreparation })));
const PreparationDetail = lazy(() => import('./pages/RolePrep/PreparationDetail').then(m => ({ default: m.PreparationDetail })));
const RecruiterDashboard = lazy(() => import('./components/Recruiter/RecruiterDashboard').then(m => ({ default: m.RecruiterDashboard })));
const RecruiterProfileSetup = lazy(() => import('./components/Recruiter/RecruiterProfileSetup').then(m => ({ default: m.RecruiterProfileSetup })));
const JobPostingsList = lazy(() => import('./components/Recruiter/JobPostingsList').then(m => ({ default: m.JobPostingsList })));
const CreateJobPosting = lazy(() => import('./components/Recruiter/CreateJobPosting').then(m => ({ default: m.CreateJobPosting })));
const JobPostingDetail = lazy(() => import('./components/Recruiter/JobPostingDetail').then(m => ({ default: m.JobPostingDetail })));
const TalentLeaderboard = lazy(() => import('./components/Recruiter/TalentLeaderboard').then(m => ({ default: m.TalentLeaderboard })));
const ShortlistPage = lazy(() => import('./pages/Recruiter/ShortlistPage').then(m => ({ default: m.ShortlistPage })));
const CandidateProfileView = lazy(() => import('./components/Recruiter/CandidateProfileView').then(m => ({ default: m.CandidateProfileView })));
const RecruiterInterviewDetailView = lazy(() => import('./components/Recruiter/RecruiterInterviewDetailView').then(m => ({ default: m.RecruiterInterviewDetailView })));
const CandidateMatchesView = lazy(() => import('./components/Recruiter/CandidateMatchesView').then(m => ({ default: m.CandidateMatchesView })));
const SavedCandidatesList = lazy(() => import('./components/Recruiter/SavedCandidatesList').then(m => ({ default: m.SavedCandidatesList })));
const CandidateDashboard = lazy(() => import('./components/Candidate/CandidateDashboard').then(m => ({ default: m.CandidateDashboard })));
const GetStarted = lazy(() => import('./pages/GetStarted').then(m => ({ default: m.GetStarted })));
const AdminModerationDashboard = lazy(() => import('./pages/AdminModerationDashboard').then(m => ({ default: m.AdminModerationDashboard })));
const LegalDocumentViewer = lazy(() => import('./components/Legal/LegalDocumentViewer'));

// SEO Landing Pages
const RoleLandingPage = lazy(() => import('./pages/Landing/templates/RoleLandingPage'));
const InterviewTypePage = lazy(() => import('./pages/Landing/templates/InterviewTypePage'));
const ComparisonPage = lazy(() => import('./pages/Landing/templates/ComparisonPage'));

// Public utility pages
const DomainVerificationPage = lazy(() => import('./pages/DomainVerificationPage').then(m => ({ default: m.DomainVerificationPage })));

// Loading fallback component
const LoadingFallback = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
    <CircularProgress />
  </Box>
);

// Cache for candidate type to prevent refetching on remount
const candidateTypeCache = { value: '', userId: '' };

// Helper component to get candidate type from user
let withCandidateTypeRenderCount = 0;
const WithCandidateType: React.FC<{ children: (candidateType: string) => React.ReactNode }> = React.memo(({ children }) => {
  withCandidateTypeRenderCount++;
  console.log(`🔵 [WithCandidateType] RENDER #${withCandidateTypeRenderCount}`);

  const { user, getAccessTokenSilently } = useAuth0();
  const [candidateType, setCandidateType] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const hasFetchedRef = React.useRef(false);

  // Keep ref updated but don't trigger re-renders when function reference changes
  const getAccessTokenRef = React.useRef(getAccessTokenSilently);
  React.useEffect(() => {
    getAccessTokenRef.current = getAccessTokenSilently;
  }, [getAccessTokenSilently]);

  useEffect(() => {
    console.log('🔵 [WithCandidateType] useEffect EXECUTING');
    const fetchCandidateType = async () => {
      console.log('🔍 [WithCandidateType] Starting candidate type fetch...');
      console.log('🔍 [WithCandidateType] user?.sub:', user?.sub);
      console.log('🔍 [WithCandidateType] hasFetchedRef.current:', hasFetchedRef.current);
      console.log('🔍 [WithCandidateType] candidateTypeCache:', candidateTypeCache);

      if (!user?.sub) {
        console.warn('⚠️ [WithCandidateType] No user sub found');
        console.log('⚠️ [WithCandidateType] Calling setLoading(false)');
        setLoading(false);
        console.log('⚠️ [WithCandidateType] Early return');
        return;
      }

      // Check cache first to prevent unnecessary fetches
      if (candidateTypeCache.userId === user.sub && candidateTypeCache.value && !hasFetchedRef.current) {
        console.log('💾 [WithCandidateType] Using cached candidate type:', candidateTypeCache.value);
        console.log('💾 [WithCandidateType] Calling setCandidateType with:', candidateTypeCache.value);
        setCandidateType(candidateTypeCache.value);
        console.log('💾 [WithCandidateType] Calling setLoading(false)');
        setLoading(false);
        hasFetchedRef.current = true;
        console.log('💾 [WithCandidateType] Cache hit - early return');
        return;
      }

      // Prevent duplicate fetches
      if (hasFetchedRef.current) {
        console.log('⏭️ [WithCandidateType] Already fetched, skipping');
        return;
      }

      hasFetchedRef.current = true;
      console.log('👤 [WithCandidateType] User ID:', user.sub);
      console.log('👤 [WithCandidateType] Set hasFetchedRef to true, proceeding with fetch...');

      try {
        // Fetch candidate type from user metadata (NOT from roles)
        const token = await getAccessTokenRef.current();
        console.log('🔑 [WithCandidateType] Got access token');

        const apiUrl = import.meta.env.VITE_API_URL || 'https://backend.veltus.io';
        const metadataUrl = `${apiUrl}/auth/user-metadata/${user.sub}`;
        console.log('🌐 [WithCandidateType] Fetching metadata from:', metadataUrl);

        const metadataResponse = await fetch(metadataUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        console.log('📡 [WithCandidateType] Metadata response status:', metadataResponse.status);

        if (metadataResponse.ok) {
          const { metadata } = await metadataResponse.json();
          console.log('📦 [WithCandidateType] Received metadata:', metadata);

          const type = metadata.candidate_type;
          console.log('🎯 [WithCandidateType] Extracted candidate_type:', type);

          if (type) {
            console.log('✅ [WithCandidateType] Calling setCandidateType with:', type);
            setCandidateType(type);
            candidateTypeCache.value = type;
            candidateTypeCache.userId = user.sub;
            console.log('✅ [WithCandidateType] Successfully set candidate type and cache:', type);
          } else {
            console.warn('⚠️ [WithCandidateType] No candidate_type in metadata, using default fallback');
            console.log('⚠️ [WithCandidateType] Calling setCandidateType with: strategist');
            setCandidateType('strategist'); // Default fallback
          }
        } else {
          console.error('❌ [WithCandidateType] Failed to fetch metadata, status:', metadataResponse.status);
          console.log('❌ [WithCandidateType] Calling setCandidateType with: strategist');
          setCandidateType('strategist'); // Default fallback
        }
      } catch (error) {
        console.error('❌ [WithCandidateType] Error fetching candidate type:', error);
        console.log('❌ [WithCandidateType] Calling setCandidateType with: strategist (error fallback)');
        setCandidateType('strategist'); // Default fallback
      } finally {
        console.log('🏁 [WithCandidateType] In finally block, calling setLoading(false)');
        setLoading(false);
        console.log('🏁 [WithCandidateType] Fetch complete, loading set to false');
      }
    };

    console.log('🔵 [WithCandidateType] About to call fetchCandidateType()');
    fetchCandidateType();
    console.log('🔵 [WithCandidateType] fetchCandidateType() called (async, may still be running)');
  }, [user]); // Removed getAccessTokenSilently from deps - using ref instead

  console.log('🔵 [WithCandidateType] After useEffect, checking loading state:', loading);
  if (loading) {
    console.log('⏳ [WithCandidateType] Still loading = true, showing loading fallback...');
    return <LoadingFallback />;
  }

  console.log('🎬 [WithCandidateType] Rendering children with candidate type:', candidateType);
  console.log('🎬 [WithCandidateType] Calling children function with candidateType:', candidateType);
  const childrenResult = children(candidateType);
  console.log('🎬 [WithCandidateType] Children function returned, type:', typeof childrenResult);
  console.log('🎬 [WithCandidateType] About to return children result');
  return <>{childrenResult}</>;
}, (prevProps, nextProps) => {
  // Custom comparison function for React.memo
  // Only re-render if children function reference changed (which it shouldn't in our case)
  const shouldSkipRender = prevProps.children === nextProps.children;
  console.log('🔵 [WithCandidateType] React.memo comparison - shouldSkipRender:', shouldSkipRender);
  return shouldSkipRender;
});


const App: React.FC = () => {
  const { isAuthenticated, user, getAccessTokenSilently } = useAuth0();
  const [isLoading, setIsLoading] = useState(true);
  const [userRole, setUserRole] = useState<string | null>(null);

  // Keep backend alive by pinging health endpoint every 40 seconds
  // useKeepAlive(); // DISABLED - not preventing Render backend shutdown

  useEffect(() => {
    const checkUserRole = async () => {
      if (!isAuthenticated || !user?.sub) {
        setIsLoading(false);
        return;
      }

      try {
        // Get user roles from the API - don't force refresh
        const roles = await getUserRoles(user.sub, getAccessTokenSilently, false);
        console.log('App: User roles:', roles);

        // Check if user is a recruiter (previously called "manager")
        const isRecruiter = roles.some(role => role.id === 'rol_ndBoJF3ROg0eczNi');

        // Check if user is an interviewer
        const isInterviewer = roles.some(role => role.id === 'rol_HXwp7bRcdBIFqp8u');

        // Check if interviewer has a candidate type by fetching metadata
        let hasCandidateType = false;
        if (isInterviewer) {
          try {
            const token = await getAccessTokenSilently();
            const apiUrl = import.meta.env.VITE_API_URL || 'https://backend.veltus.io';
            const metadataResponse = await fetch(`${apiUrl}/auth/user-metadata/${user.sub}`, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });

            if (metadataResponse.ok) {
              const { metadata } = await metadataResponse.json();
              hasCandidateType = !!metadata.candidate_type;
            }
          } catch (error) {
            console.error('App: Error fetching candidate type from metadata:', error);
          }
        }

        if (isRecruiter) {
          setUserRole('recruiter');
        } else if (isInterviewer) {
          if (hasCandidateType) {
            setUserRole('interviewer');
          } else {
            setUserRole('interviewer_no_type');
          }
        } else {
          setUserRole('no_role');
        }
      } catch (error) {
        console.error('App: Error checking user role:', error);
        // For new users or errors, treat as no role and let them set up their profile
        setUserRole('no_role');
      } finally {
        setIsLoading(false);
      }
    };

    checkUserRole();
  }, [isAuthenticated, user, getAccessTokenSilently]);
  
  console.log('Authentication state:', isAuthenticated);
  console.log('User role:', userRole);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <ThemeContextProvider>
      <TourProvider>
        <FloatingChatProvider>
          <Router>
            <Toaster position="top-right" />
            <ProductTour />

          {/* Onboarding Flow - Shows for new users */}
          {isAuthenticated && (
            <Suspense fallback={null}>
              <OnboardingFlow />
            </Suspense>
          )}

          {/* Floating Chat - Shows for interviewer users */}
          {isAuthenticated && <FloatingChatWrapper userRole={userRole} />}

          <Layout>
          <Suspense fallback={<LoadingFallback />}>
          <Routes>
            {/* Root route - redirects based on user role */}
            <Route
              path="/"
              element={
                isAuthenticated ?
                  (userRole === 'recruiter' ?
                    <Navigate to="/recruiter-dashboard" replace /> :
                    (userRole === 'interviewer' || userRole === 'interviewer_no_type') ?
                      <Navigate to="/dashboard" replace /> :
                      <Navigate to="/get-started" replace />
                  ) :
                  <LandingPage />
              }
            />

            {/* Onboarding - Role Selection */}
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute>
                  <RoleSelectionOnboarding />
                </ProtectedRoute>
              }
            />

            {/* Get Started Page */}
            <Route
              path="/get-started"
              element={
                <ProtectedRoute>
                  <GetStarted />
                </ProtectedRoute>
              }
            />

            {/* Candidate Dashboard */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <CandidateDashboard />
                </ProtectedRoute>
              }
            />

            {/* Protected routes */}
            <Route
              path="/select-role"
              element={
                <ProtectedRoute>
                  <div /> {/* Empty div as we handle role selection in Layout */}
                </ProtectedRoute>
              }
            />

            <Route
              path="/recruiter-dashboard"
              element={
                <RecruiterRoute>
                  <RecruiterDashboard />
                </RecruiterRoute>
              }
            />

            <Route
              path="/recruiter/profile/setup"
              element={
                <RecruiterRoute>
                  <RecruiterProfileSetup />
                </RecruiterRoute>
              }
            />

            <Route
              path="/recruiter/profile/edit"
              element={
                <RecruiterRoute>
                  <RecruiterProfileSetup />
                </RecruiterRoute>
              }
            />

            <Route
              path="/recruiter/jobs"
              element={
                <RecruiterRoute>
                  <JobPostingsList />
                </RecruiterRoute>
              }
            />

            <Route
              path="/recruiter/jobs/new"
              element={
                <RecruiterRoute>
                  <CreateJobPosting />
                </RecruiterRoute>
              }
            />

            <Route
              path="/recruiter/jobs/:jobId"
              element={
                <RecruiterRoute>
                  <JobPostingDetail />
                </RecruiterRoute>
              }
            />

            <Route
              path="/recruiter/jobs/:jobId/edit"
              element={
                <RecruiterRoute>
                  <CreateJobPosting />
                </RecruiterRoute>
              }
            />

            <Route
              path="/recruiter/jobs/:jobId/matches"
              element={
                <RecruiterRoute>
                  <CandidateMatchesView />
                </RecruiterRoute>
              }
            />

            <Route
              path="/recruiter/talent"
              element={
                <RecruiterRoute>
                  <TalentLeaderboard />
                </RecruiterRoute>
              }
            />

            <Route
              path="/recruiter/talent/:userId"
              element={
                <RecruiterRoute>
                  <CandidateProfileView />
                </RecruiterRoute>
              }
            />

            <Route
              path="/recruiter/shortlist"
              element={
                <RecruiterRoute>
                  <ShortlistPage />
                </RecruiterRoute>
              }
            />

            <Route
              path="/recruiter/candidates/:userId/interviews/:interviewId"
              element={
                <RecruiterRoute>
                  <RecruiterInterviewDetailView />
                </RecruiterRoute>
              }
            />

            <Route
              path="/recruiter/saved"
              element={
                <RecruiterRoute>
                  <SavedCandidatesList />
                </RecruiterRoute>
              }
            />

            {/* Legacy route - redirect old manager-dashboard to recruiter-dashboard */}
            <Route
              path="/manager-dashboard"
              element={<Navigate to="/recruiter-dashboard" replace />}
            />

            {/* Interview selection page */}
            <Route
              path="/interviews"
              element={
                <ProtectedRoute>
                  <InterviewSelection />
                </ProtectedRoute>
              }
            />

            {/* Analytical interview */}
            <Route
              path="/interview/analytical"
              element={
                <ProtectedRoute>
                  <WithCandidateType>
                    {(candidateType) => (
                      <UnifiedInterviewPanel interviewType="analytical" candidateType={candidateType} />
                    )}
                  </WithCandidateType>
                </ProtectedRoute>
              }
            />

            {/* Coding challenge */}
            <Route
              path="/interview/coding"
              element={
                <ProtectedRoute>
                  <CodingChallengePanel candidateType="engineer" />
                </ProtectedRoute>
              }
            />

            {/* Finance interview */}
            <Route
              path="/interview/finance"
              element={
                <ProtectedRoute>
                  <WithCandidateType>
                    {(candidateType) => (
                      <UnifiedInterviewPanel interviewType="finance" candidateType={candidateType} />
                    )}
                  </WithCandidateType>
                </ProtectedRoute>
              }
            />

            {/* Design interview */}
            <Route
              path="/interview/design"
              element={
                <ProtectedRoute>
                  <WithCandidateType>
                    {(candidateType) => (
                      <UnifiedInterviewPanel interviewType="design" candidateType={candidateType} />
                    )}
                  </WithCandidateType>
                </ProtectedRoute>
              }
            />

            {/* Marketing interview */}
            <Route
              path="/interview/marketing"
              element={
                <ProtectedRoute>
                  <WithCandidateType>
                    {(candidateType) => (
                      <UnifiedInterviewPanel interviewType="marketing" candidateType={candidateType} />
                    )}
                  </WithCandidateType>
                </ProtectedRoute>
              }
            />

            {/* Legacy /interview route - redirect to selection */}
            <Route
              path="/interview"
              element={<Navigate to="/interviews" replace />}
            />

            {/* Legacy /coding route - redirect to new path */}
            <Route
              path="/coding"
              element={<Navigate to="/interview/coding" replace />}
            />

            <Route
              path="/interview-history"
              element={
                <ProtectedRoute>
                  <InterviewHistory />
                </ProtectedRoute>
              }
            />

            {/* Interview history with specific interview ID */}
            <Route
              path="/interview-history/:id"
              element={
                <ProtectedRoute>
                  <InterviewHistory />
                </ProtectedRoute>
              }
            />

            {/* Interview details - note this must come after /interview/analytical and /interview/coding */}
            <Route
              path="/interview/:interviewId"
              element={
                <ProtectedRoute>
                  <InterviewDetails />
                </ProtectedRoute>
              }
            />

            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />

            {/* Admin Moderation Dashboard */}
            <Route
              path="/admin/moderation"
              element={
                <AdminRoute>
                  <AdminModerationDashboard />
                </AdminRoute>
              }
            />

            {/* Role Prep routes */}
            <Route
              path="/role-prep"
              element={
                <ProtectedRoute>
                  <RolePrepList />
                </ProtectedRoute>
              }
            />

            <Route
              path="/role-prep/new"
              element={
                <ProtectedRoute>
                  <CreatePreparation />
                </ProtectedRoute>
              }
            />

            <Route
              path="/role-prep/:id"
              element={
                <ProtectedRoute>
                  <PreparationDetail />
                </ProtectedRoute>
              }
            />

            {/* Legal Documents - Public (no authentication required) */}
            <Route
              path="/legal/:slug"
              element={<LegalDocumentViewer />}
            />

            {/* SEO Landing Pages - Role-based */}
            <Route path="/software-engineer-interview-prep" element={<RoleLandingPage />} />
            <Route path="/data-scientist-interview-prep" element={<RoleLandingPage />} />
            <Route path="/product-manager-interview-prep" element={<RoleLandingPage />} />
            <Route path="/consultant-interview-prep" element={<RoleLandingPage />} />
            <Route path="/financial-analyst-interview-prep" element={<RoleLandingPage />} />
            <Route path="/marketing-manager-interview-prep" element={<RoleLandingPage />} />

            {/* SEO Landing Pages - Interview type */}
            <Route path="/coding-interview-practice" element={<InterviewTypePage />} />
            <Route path="/case-interview-preparation" element={<InterviewTypePage />} />
            <Route path="/technical-interview-simulator" element={<InterviewTypePage />} />
            <Route path="/behavioral-interview-coach" element={<InterviewTypePage />} />
            <Route path="/system-design-interview-prep" element={<InterviewTypePage />} />

            {/* Comparison Pages - Public SEO landing pages */}
            <Route path="/vs/leetcode" element={<ComparisonPage />} />
            <Route path="/vs/pramp" element={<ComparisonPage />} />
            <Route path="/vs/interviewing-io" element={<ComparisonPage />} />
            <Route path="/alternatives/interview-prep-platforms" element={<ComparisonPage />} />

            {/* Public utility pages */}
            <Route path="/verify-domain" element={<DomainVerificationPage />} />

            {/* Catch all route - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
        </Layout>
      </Router>
        </FloatingChatProvider>
      </TourProvider>
    </ThemeContextProvider>
  );
};

export default App;
